<template lang="pug">
section#actors-index.content-page
    segment(v-bind:title="$t('actors.search.title')")
        actor-query-options(
            ref="queryOptions"
            mode="index"
            v-on:search="onSearch"
            v-bind:autofocus="true")


    //- registration promotion for anonymous users
    promotion-no-results-call-to-action.is-parent(
        v-if="promotionPageReached"
        v-bind:anonymous-only="true")
        template(#bubble-title="")
            h3.bubble-title {{ $t('actors.loginToFindMore') }}


    actor-highlights(
        ref="actorHighlights"
        v-bind:title="$t('actors.prominent')"
        v-bind:items="actorsHighlights"
        v-bind:show-artworks="false"
        v-on:error="error = $event")


    segment(
        class-name="actors-others"
        v-bind:title="$t('actors.others')"
        v-if="actors.length")

        div.columns.other-actors
            loading-spinner(v-if="!readyAllActors && !error")
            slick-slides(
                v-bind:options="slickOptions1"
                v-bind:items="actors")
                template(#slide-card="slotScope")
                    actor-card(
                        role="article"
                        v-bind:item="slotScope.item"
                        v-bind:link-to="true"
                        v-bind:image-as-background="false"
                        v-bind:social="false")


    artwork-spotlights(
        v-if="heroImages.length && readyAllActors"
        v-bind:title="$t('actors.prominent')"
        v-bind:images="heroImages"
        card-component="artwork-card")


    loading-spinner(v-if="!ready && !error")

    errors-message(v-if="error" v-bind:error="error")

</template>

<script>
import IndexPage from '@/components/Base/IndexPage';
import ActorQueryOptions from '@/components/Actor/QueryOptions';
import SearchMixin from '@/components/Base/SearchMixin';

export default {
    name: 'actors-index',

    extends: IndexPage,

    mixins: [ SearchMixin ],

    components: {
        ActorQueryOptions,
        ArtworkSpotlights: () => import('@/components/Artwork/Spotlights'),
        ActorHighlights: () => import('@/components/Actor/Highlights'),
        PromotionNoResultsCallToAction: () => import('@/components/global/PromotionNoResultsCallToAction.vue')
    },

    data: function () {
        return {
            pageMetaInfo: {
                title: this.$t('navigation.actors')
            },
            actors: [],
            actorsHighlights: [],
            heroImages: [],
            vonWrights: [],
            readyAllActors: false,
            slickOptions1: {
                slidesToShow: 5,
                slidesToScroll: 5,
                rows: 1,
                arrows: true,
                variableWidth: false,
                adaptiveHeight: false,
                autoplay: false,
                autoplaySpeed: 3000,
                speed: 500,
                responsive: [
                    {
                        breakpoint: 1024,
                        settings: {
                            slidesToShow: 4,
                            slidesToScroll: 4,
                            infinite: true,
                            dots: true
                        }
                    },
                    {
                        breakpoint: 850,
                        settings: {
                            slidesToShow: 3,
                            slidesToScroll: 3,
                            infinite: true,
                            dots: true
                        }
                    },
                    {
                        breakpoint: 700,
                        settings: {
                            slidesToShow: 2,
                            slidesToScroll: 2,
                            infinite: true,
                            dots: true
                        }
                    },
                    {
                        breakpoint: 480,
                        settings: {
                            slidesToShow: 1,
                            slidesToScroll: 1,
                            infinite: true,
                            dots: true
                        }
                    }
                ]
            }
        };
    },

    mounted: function () {
        this.checkPromotionLimit();

        // load ActorHighlights
        this.getHighlights()
            .then((items) => {
                this.actorsHighlights = _.compact(items);
            })
            .catch((error) => {
                this.error = error;
            })
            .finally(() => {
                this.loadViewData();
            });
    },

    watch: {
        svcPages: function () {
            this.getArtworks();
        }
    },

    methods: {
        /**
         * Load view data after the highlights have loaded.
         */
        loadViewData: async function () {
            return this.getActors()
                .then(() => {

                    // recommended artworks
                    this.getArtworks()
                        .then(() => {
                            this.ready = true;
                            this.$eventBus.$emit('page-ready');
                        })
                        .catch((error) => {
                            this.error = error;
                            this.$eventBus.$emit('page-error', error);
                        });

                })
                .catch((error) => {
                    this.error = error;
                    this.$eventBus.$emit('page-error', this.error);
                });
        },

        getActors: async function () {
            this.actors = [];
            this.readyAllActors = false;

            return this.$SVC.queryActors({
                AutoWildcards: false,
                EnableFacetedSearch: false,
                UseProfile: this.getIndexQueryUseProfile(),
                // PreferredLocale included by svc helpers
                PageIndex: 1,
                PageSize: 16,
                // HasImage: false,
                // SortField: 'TopicDate:desc,ReleaseDate:desc', // automatic relevance
                Template: {
                    PrimaryProperties: true,
                    IndexProperties: true,
                    ObjectProperties: false,
                    IngressProperties: false,
                    CuratorProperties: false,
                    ArtworkProperties: false,
                    DetailProperties: false,
                    ImageInfoExtended: true
                }
            })
                .then((response) => {
                    this.actors = (response && response.data && response.data.Results) || [];
                    this.readyAllActors = true;
                });
        },

        /**
         * Artworks are taken from /svc/pages
         */
        getArtworks: function () {
            if (this.svcPages.Artworks) {
                this.heroImages = _.take(_.shuffle(this.svcPages.Artworks), 2);
            }
            return Promise.resolve();
        },

        onSearch: function (options) {
            const text = (options && options.Text) || '';
            this.$router.push({name: `${this.$i18n.locale}.ActorsSearch`, query: {Text: text}});
        },

        /**
         * Check if user is anonymous to show promotion message.
         */
        checkPromotionLimit: function () {
            this.promotionPageReached = this.isPromotionPageReached(0, 0, this.levelBasicUser);
        }
    }
};
</script>

<style lang="scss">
// @import '~assets/styles/mixins';

#actors-index {
    .actor-query-options {
        margin-bottom: 2rem;
    }
    .option {
        margin: 0 1rem 0.5rem 0;
    }
    .other-actors {
        display: block;
        flex-wrap: wrap;
        .slick-track {
            display: flex;
            min-height: unset;
        }
        .slick-list {
            padding-left: 1rem;
        }
        .slick-dots {
            margin-bottom: 0;
        }
        .slick-dotted.slick-slider {
            margin-bottom: 50px;
        }
        .slick-slide {
            display: inline-flex;
            float: none;
            &.slick-active > div:last-child {
                margin: 0 auto;
            }
        }
        .card {
            align-items: center;
            margin: 0 auto;
            text-align: center;
            white-space: break;
            .card-header {
                margin: 1rem auto;
                h2 {
                    white-space: normal;
                }
                .card-header-title {
                    text-align: center;
                }
            }
        }
    }

    .artwork-spotlights {
        // both heigths are required!
        .card-image {
            height: 800px;
            .image {
                height: 800px;
            }
        }
    }
}
</style>
